import { Injectable } from '@angular/core';

export const STP_CONSTRAINTS = {
  sourceLumpsum: { min: 10_000, max: 100_000_000, step: 10_000 },
  monthlyTransfer: { min: 500, max: 1_000_000, step: 500 },
  sourceRate: { min: 1, max: 15, step: 0.1 },
  destRate:   { min: 1, max: 30, step: 0.1 },
  tenureYears: { min: 1, max: 30, step: 1 }
} as const;

export interface StpMonthRow {
  month: number;
  sourceBalance: number;
  destBalance: number;
  transferred: number;
}

export interface StpYearRow {
  year: number;
  sourceBalance: number;
  destBalance: number;
  transferredThisYear: number;
}

export interface StpResult {
  months: StpMonthRow[];
  years: StpYearRow[];
  totalTransferred: number;
  finalSourceValue: number;
  finalDestValue: number;
  destGain: number;
  combinedValue: number;
}

@Injectable({ providedIn: 'root' })
export class StpCalculatorService {

  calculate(
    sourceLumpsum: number,
    monthlyTransfer: number,
    sourceAnnualRate: number,
    destAnnualRate: number,
    tenureYears: number
  ): StpResult {
    const sourceMonthlyRate = sourceAnnualRate / 100 / 12;
    const destMonthlyRate   = destAnnualRate / 100 / 12;
    const totalMonths = tenureYears * 12;

    let sourceBalance = sourceLumpsum;
    let destBalance = 0;
    let totalTransferred = 0;

    const months: StpMonthRow[] = [];

    for (let m = 1; m <= totalMonths; m++) {
      sourceBalance += sourceBalance * sourceMonthlyRate;
      const transfer = Math.min(monthlyTransfer, sourceBalance);
      sourceBalance -= transfer;

      destBalance += destBalance * destMonthlyRate;
      destBalance += transfer;

      totalTransferred += transfer;

      months.push({ month: m, sourceBalance, destBalance, transferred: transfer });

      if (sourceBalance <= 0.5) break;
    }

    const years: StpYearRow[] = [];
    for (let y = 1; y <= Math.ceil(months.length / 12); y++) {
      const yearMonths = months.filter(m => Math.ceil(m.month / 12) === y);
      const last = yearMonths[yearMonths.length - 1];
      years.push({
        year: y,
        sourceBalance: last.sourceBalance,
        destBalance: last.destBalance,
        transferredThisYear: yearMonths.reduce((s, m) => s + m.transferred, 0)
      });
    }

    const finalSourceValue = sourceBalance;
    const finalDestValue = destBalance;

    return {
      months,
      years,
      totalTransferred,
      finalSourceValue,
      finalDestValue,
      destGain: finalDestValue - totalTransferred,
      combinedValue: finalSourceValue + finalDestValue
    };
  }
}
